import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'

import { useCreateSupportRequest, useReplySupportRequest, useSettleSupportRequest } from '@/api/support'
import { useAuthenticatedFirebaseUser } from '@/auth/useFirebaseUser'
import { SupportPanel } from '@/components/organisms/SupportPanel'
import { useNow } from '@/hooks/useNow'
import { useSupportContext } from '@/hooks/useSupportContext'
import { regionQuery } from '@/state/regions/region'
import { useSupportPanelStore } from '@/state/support/supportPanelStore'
import { requesterSupportRequestItemsQuery, supportRequestQuery } from '@/state/support/supportRequests'
import { type SupportRequestDocument } from '@/state/support/types'
import { type SupportLoadState, useLoadState } from '@/state/support/useRequesterSupportRequests'
import { useSupportReads } from '@/state/support/useSupportReads'

import { fullName } from './supportAgentItems'
import { requesterThread, requestRow } from './supportRequesterItems'
import { useSupportLabels } from './useSupportLabels'

const useCentralName = () => {
  const { regionId } = useAuthenticatedFirebaseUser()
  const { data: region } = useQuery(regionQuery(regionId))
  return region?.data?.name ?? ''
}

interface RequestsPanelProps {
  readonly requests: ReadonlyArray<SupportRequestDocument>
  readonly loadState: SupportLoadState
  readonly onRetry: () => void
}

const RequesterRequestsPanel = ({ requests, loadState, onRetry }: RequestsPanelProps) => {
  const intl = useIntl()
  const labels = useSupportLabels()
  const now = useNow()
  const { uid } = useAuthenticatedFirebaseUser().firebaseUser
  const centralName = useCentralName()
  const { isUnreadByRequester } = useSupportReads()
  const { closePanel, showNewRequest, showConversation } = useSupportPanelStore()

  const rows = useMemo(
    () =>
      requests.map((request) => requestRow(request, { uid, unread: isUnreadByRequester(request), intl, labels, now })),
    [requests, isUnreadByRequester, uid, intl, labels, now]
  )

  if (loadState === 'unavailable') {
    return <SupportPanel view="unavailable" onRetry={onRetry} onClose={closePanel} />
  }
  if (loadState === 'loading') {
    return <SupportPanel view="loading" onClose={closePanel} />
  }
  return (
    <SupportPanel
      view="requests"
      centralName={centralName}
      list={{ rows, onSelect: showConversation }}
      onNewRequest={showNewRequest}
      onClose={closePanel}
    />
  )
}

const RequesterNewRequestPanel = () => {
  const labels = useSupportLabels()
  const { permissions } = useAuthenticatedFirebaseUser()
  const centralName = useCentralName()
  const context = useSupportContext()
  const { draft, updateDraft, discardDraft, showRequests, showConversation, closePanel } = useSupportPanelStore()
  const createRequest = useCreateSupportRequest()

  return (
    <SupportPanel
      view="new"
      onClose={closePanel}
      form={{
        values: draft,
        context: { you: labels.you({ permissions, centralName }), page: context.page, system: context.system },
        submitting: createRequest.isPending,
        error: createRequest.isError,
        onChange: updateDraft,
        onBack: showRequests,
        onCancel: discardDraft,
        onSubmit: () => {
          const { category, blocking, text } = draft
          if (!category || !blocking) {
            return
          }
          createRequest.mutate(
            { category, blocking, text: text.trim(), context },
            {
              onSuccess: ({ number }) => {
                discardDraft()
                showConversation(number)
              },
            }
          )
        },
      }}
    />
  )
}

const RequesterConversationPanel = ({ number }: { readonly number: number }) => {
  const intl = useIntl()
  const labels = useSupportLabels()
  const now = useNow()
  const { uid } = useAuthenticatedFirebaseUser().firebaseUser
  const { closePanel, showRequests } = useSupportPanelStore()
  const { lastReadAt, markRead } = useSupportReads()
  const [readUpTo] = useState(() => lastReadAt(number))

  const request = useQuery(supportRequestQuery(number))
  const items = useQuery(requesterSupportRequestItemsQuery(number))
  const reply = useReplySupportRequest()
  const settle = useSettleSupportRequest()

  const document = request.data?.data
  const lastAgentReplyAt = document?.lastAgentReplyAt
  useEffect(() => {
    if (lastAgentReplyAt) {
      markRead(number, lastAgentReplyAt)
    }
  }, [number, lastAgentReplyAt, markRead])

  const queryClient = useQueryClient()
  const { state: loadState, restart } = useLoadState([request, items])

  const threadItems = useMemo(
    () => requesterThread(items.data?.data ?? [], { uid, readUpTo, intl }),
    [items.data, uid, readUpTo, intl]
  )

  if (loadState === 'unavailable') {
    return (
      <SupportPanel
        view="unavailable"
        onRetry={() => {
          restart()
          handleNotAwaitedPromise(queryClient.resetQueries({ queryKey: ['supportRequests', number] }))
        }}
        onClose={closePanel}
      />
    )
  }
  if (!document) {
    return <SupportPanel view="loading" onClose={closePanel} />
  }

  return (
    <SupportPanel
      view="conversation"
      onClose={closePanel}
      conversation={{
        meta: intl.formatMessage(
          { id: 'soutien.conversation.meta' },
          {
            number: document.number,
            category: labels.category(document.category),
            blocking: labels.blocking(document.blocking),
            author: fullName(document.author),
          }
        ),
        title: document.title,
        requesterStatus: document.requesterStatus,
        assigneeLine: document.assignee
          ? intl.formatMessage(
              { id: 'soutien.conversation.assignee' },
              { firstName: document.assignee.firstName, when: labels.openedAt(document.createdAt, now) }
            )
          : intl.formatMessage({ id: 'soutien.conversation.unassigned' }),
        contextLine: [document.context.page, labels.you(document.context), document.context.system]
          .filter(Boolean)
          .join(' · '),
        items: threadItems,
        replying: reply.isPending,
        replyError: reply.isError,
        settling: settle.isPending,
        settleError: settle.isError,
        onBack: showRequests,
        onReply: async (text) => {
          try {
            await reply.mutateAsync({ number, text: text.trim() })
            return true
          } catch {
            return false
          }
        },
        onSettle: () => settle.mutate({ number }),
      }}
    />
  )
}

export const RequesterSupportPanel = (props: RequestsPanelProps) => {
  const view = useSupportPanelStore((state) => state.view)
  switch (view.kind) {
    case 'new':
      return <RequesterNewRequestPanel />
    case 'conversation':
      return <RequesterConversationPanel key={view.number} number={view.number} />
    case 'requests':
      return <RequesterRequestsPanel {...props} />
  }
}
